import {
    collection,
    getDocs,
    addDoc,
    query,
    orderBy,
    where,
    doc,
    getDoc,
    updateDoc,
    deleteDoc
} from 'firebase/firestore';
import { db } from '../database/firebase';

/**
 * Log shape:
 * {
 *   productId: string,
 *   productName: string,
 *   date: string (ISO date YYYY-MM-DD),
 *   quantityChange: number (positive for additions, negative for consumption),
 *   previousStock: number,
 *   newStock: number,
 *   transactionType: string (e.g. "Store Sale", "Admin Recharge - Guest", "Purchase Topup"),
 *   referenceId: string (e.g. Bill No, Order ID),
 *   customerName: string (optional),
 *   createdAt: string (ISO timestamp)
 * }
 */

/**
 * Add a new inventory log record to Firestore
 * @param {Object} logData
 * @returns {Promise<string>} Document ID
 */
export const addInventoryLog = async (logData) => {
    try {
        const logsRef = collection(db, 'inventoryLogs');
        const docRef = await addDoc(logsRef, {
            ...logData,
            createdAt: new Date().toISOString(),
        });
        return docRef.id;
    } catch (error) {
        console.error('Error adding inventory log:', error);
        throw error;
    }
};

/**
 * Fetch all inventory logs from Firestore, newest first
 * @returns {Promise<Array>}
 */
export const getAllInventoryLogs = async () => {
    try {
        const logsRef = collection(db, 'inventoryLogs');
        const q = query(logsRef, orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
        console.error('Error fetching inventory logs:', error);
        throw error;
    }
};

/**
 * Fetch logs within a date range (ISO date strings "YYYY-MM-DD")
 * @param {string} from
 * @param {string} to
 * @returns {Promise<Array>}
 */
export const getInventoryLogsByDateRange = async (from, to) => {
    try {
        const logsRef = collection(db, 'inventoryLogs');
        const q = query(
            logsRef,
            where('date', '>=', from),
            where('date', '<=', to),
            orderBy('date', 'desc')
        );
        const snap = await getDocs(q);
        const results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Since we order by date, sort by createdAt descending within the same date
        return results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } catch (error) {
        console.error('Error fetching inventory logs by date range:', error);
        throw error;
    }
};

/**
 * Delete an inventory log and revert the stock change on the product
 * @param {string} logId 
 * @returns {Promise<boolean>}
 */
export const deleteInventoryLogAndRevertStock = async (logId) => {
    try {
        const logRef = doc(db, 'inventoryLogs', logId);
        const logSnap = await getDoc(logRef);
        
        if (!logSnap.exists()) {
            throw new Error("Log not found");
        }
        
        const logData = logSnap.data();
        const quantityChange = logData.quantityChange || 0;
        
        if (logData.productId && quantityChange !== 0) {
            const productRef = doc(db, 'products', logData.productId);
            const productSnap = await getDoc(productRef);
            
            if (productSnap.exists()) {
                const currentStock = productSnap.data().stockCount || 0;
                // Revert the change: if it was +10 (added), subtract 10. If it was -5 (consumed), add 5.
                const revertedStock = Math.max(0, currentStock - quantityChange);
                
                await updateDoc(productRef, { stockCount: revertedStock });
            }
        }
        
        await deleteDoc(logRef);
        return true;
    } catch (error) {
        console.error('Error deleting inventory log and reverting stock:', error);
        throw error;
    }
};
